import axios from "axios";
import swal from "sweetalert";
import { API_BASE_URL, getPublicUrl } from "./Utils";
import { isAbortError } from "./asyncRequestGuards";

const http = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (process.env.NODE_ENV === "development") {
        console.log("[HTTP] Token added to request:", config.url);
      }
    } else if (process.env.NODE_ENV === "development") {
      console.warn("[HTTP] No token found for request:", config.url);
    }
    // Let axios set multipart/form-data with boundary when sending FormData
    if (config.data && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAbortError(error)) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[HTTP] 401 error - Invalid or expired token for:", error.config?.url);
        const token = localStorage.getItem("accessToken");
        console.log("[HTTP] Current token:", token ? "Present" : "Missing");
      }
      
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("authChange"));
      
      const currentPath = window.location.pathname;
      if (!currentPath.includes('auth-login')) {
        swal("Session expired", "Please sign in again.", "error").then(() => {
          window.location.href = getPublicUrl("auth-login");
        });
      }
    } else if (error.response?.status === 403) {
      swal("Access denied", "You do not have permission for this action.", "error");
    } else if (error.response?.status === 429) {
      swal("Too many requests", "Please wait a moment and try again.", "warning");
    } else if (error.response?.status >= 500) {
      // Dashboards fire many parallel stats calls — one failing route should not block the whole UI.
      if (error.config?.suppressServerErrorSwal) {
        return Promise.reject(error);
      }
      const rid =
        error.response?.headers?.["x-request-id"] ||
        error.response?.headers?.["X-Request-Id"] ||
        error.response?.data?.requestId;
      const apiErr = error.response?.data?.error;
      const detail = rid ? ` Reference: ${rid}` : "";
      const devStack =
        process.env.NODE_ENV === "development" && error.response?.data?.stack
          ? `\n\n${String(error.response.data.stack).split("\n").slice(0, 4).join("\n")}`
          : "";
      const extraMsg =
        apiErr && String(apiErr).trim() && apiErr !== "Internal server error"
          ? `\n\n${apiErr}`
          : "";
      swal("Server error", `Please try again later.${detail}${extraMsg}${devStack}`, "error");
    } else if (!error.config?.suppressInlineErrorSwal && error.response?.data?.error) {
      swal("Error", error.response.data.error, "error");
    } else if (!error.config?.suppressInlineErrorSwal && error.message) {
      swal("Error", error.message, "error");
    }
    return Promise.reject(error);
  }
);

export default http;